import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Video, CheckCircle2, Wallet, CreditCard, FileDown } from "lucide-react";

export default function PatientAppointments() {
  const [appts, setAppts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/appointments");
      setAppts(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (a) => {
    const hoursLeft = (new Date(a.start) - new Date()) / 36e5;
    if (a.payment_status === "card_registered" && hoursLeft < 24) {
      if (!window.confirm(`Annulation à moins de 24 h : les honoraires (${a.fee_eur} €) peuvent être facturés par le praticien. Confirmer l'annulation ?`)) return;
    }
    try {
      const { data } = await api.delete(`/appointments/${a.appointment_id}`);
      toast.success(data?.late_cancellation ? "RDV annulé (annulation tardive — le praticien décidera de la facturation)" : "RDV annulé — aucun débit");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const downloadReceipt = async (a) => {
    try {
      const res = await api.get(`/appointments/${a.appointment_id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `note-honoraires-${a.start.slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const registerCard = async (id) => {
    try {
      const { data } = await api.post(`/appointments/${id}/card-setup`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const upcoming = appts.filter((a) => a.status === "confirmed" && new Date(a.start) > new Date());
  const past = appts.filter((a) => a.status !== "confirmed" || new Date(a.start) <= new Date());

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Mes rendez-vous</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Vos consultations</h1>
      </div>

      <section className="mb-10">
        <h2 className="font-display text-xl font-medium mb-4">À venir</h2>
        {upcoming.length === 0 ? (
          <div className="text-slate-500 text-sm p-8 border border-dashed border-slate-300 rounded-xl text-center">
            Aucun RDV à venir.
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((a) => (
              <div key={a.appointment_id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`appt-${a.appointment_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-50 text-[#0FACD7] flex items-center justify-center font-display font-semibold flex-shrink-0">
                    {(a.doctor?.name || "?").replace(/^Dr\s+/, "").charAt(0)}
                  </div>
                  <div>
                    <div className="font-display font-semibold flex items-center gap-2">
                      {a.doctor?.name}
                      {a.paid && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Payé {a.amount_eur ? `· ${a.amount_eur}€` : ""}</span>}
                      {a.payment_status === "card_registered" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold flex items-center gap-1" data-testid={`card-badge-${a.appointment_id}`}><CreditCard className="h-3 w-3" />CB •••• {a.card_last4} · débit {a.fee_eur} € après la consult</span>}
                      {a.mode === "cabinet" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">Au cabinet</span>}
                    </div>
                    <div className="text-sm text-slate-500">{a.doctor?.specialty}</div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(a.start).toLocaleDateString("fr-FR", { dateStyle: "long" })}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {new Date(a.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {a.doctor?.address || a.doctor?.city}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {a.payment_status === "card_required" && (
                    <Button onClick={() => registerCard(a.appointment_id)} className="btn-coral font-semibold" data-testid={`register-card-${a.appointment_id}-btn`}>
                      <CreditCard className="h-4 w-4 mr-1.5" />Enregistrer ma carte
                    </Button>
                  )}
                  <Link to={`/video/${a.appointment_id}`}>
                    <Button variant="outline" className="border-[#0FACD7] text-[#0FACD7] hover:bg-cyan-50 font-semibold" data-testid={`video-${a.appointment_id}-btn`}>
                      <Video className="h-4 w-4 mr-1.5" />Téléconsult.
                    </Button>
                  </Link>
                  <Button variant="ghost" onClick={() => cancel(a)} className="text-slate-500" data-testid={`cancel-${a.appointment_id}-btn`}>
                    Annuler
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl font-medium mb-1">Historique</h2>
        <p className="text-xs text-slate-500 mb-4">Pour vos téléconsultations réglées en ligne, téléchargez la note d'honoraires à joindre à votre feuille de soins (remboursement Sécu / mutuelle).</p>
        {past.length === 0 ? (
          <div className="text-slate-500 text-sm">Aucun historique.</div>
        ) : (
          <div className="space-y-2">
            {past.map((a) => (
              <div key={a.appointment_id} className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm">
                <div>
                  <span className="font-medium">{a.doctor?.name}</span>
                  <span className="text-slate-500"> · {new Date(a.start).toLocaleDateString("fr-FR")}</span>
                </div>
                <div className="flex items-center gap-3">
                  {a.payment_status === "paid" && (
                    <>
                      <span className="text-xs text-emerald-700 font-semibold">Payé {a.amount_eur} € par CB</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadReceipt(a)} data-testid={`receipt-${a.appointment_id}-btn`}>
                        <FileDown className="h-3.5 w-3.5 mr-1" />Note d'honoraires
                      </Button>
                    </>
                  )}
                  {a.payment_status === "refunded" && <span className="text-xs text-amber-700 font-semibold">Remboursé</span>}
                  {a.status !== "cancelled" && a.doctor?.payment_link && !a.doctor?.card_payments_enabled && !a.paid && (
                    <a href={a.doctor.payment_link} target="_blank" rel="noreferrer">
                      <Button size="sm" className="btn-coral font-semibold h-7 text-xs" data-testid={`pay-link-${a.appointment_id}-btn`}>
                        <Wallet className="h-3.5 w-3.5 mr-1" />Régler {a.doctor.consultation_fee} €
                      </Button>
                    </a>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "cancelled" ? "bg-slate-200 text-slate-600" : "bg-emerald-50 text-emerald-800"}`}>
                    {a.status === "cancelled" ? (a.late_cancellation ? "Annulé (tardif)" : "Annulé") : "Terminé"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
